import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  FileText, CheckCircle, AlertTriangle,
  Search, Clock, X, ChevronRight, Shield, Users, BookOpen
} from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;
const ax  = () => ({
  headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` }
});

const CAT_ICONS = {
  "Leave Policy":"🏖️","Attendance Policy":"⏰","Code of Conduct":"📋",
  "Offer Letter Terms":"📄","Termination Policy":"🚪","Anti-Harassment Policy":"🛡️",
  "Data Privacy Policy":"🔒","IT & Security Policy":"💻","Travel & Expense Policy":"✈️",
  "Performance Review Policy":"⭐","Grievance Policy":"📣","General Policy":"📌",
};

export default function EmployeeDocuments() {
  const { T } = useTheme();
  const CAT_COLOR = {
    "Leave Policy":             T.purple,
    "Attendance Policy":        T.cyan,
    "Code of Conduct":          T.amber,
    "Offer Letter Terms":       T.green,
    "Termination Policy":       T.red,
    "Anti-Harassment Policy":   T.purple,
    "Data Privacy Policy":      T.green,
    "IT & Security Policy":     T.cyan,
    "Travel & Expense Policy":  T.amber,
    "Performance Review Policy":T.amber,
    "Grievance Policy":         "#FF6B35",
    "General Policy":           T.muted,
  };

  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [catFilter, setCat]   = useState("all");
  const [viewing, setViewing] = useState(null);
  const [acking,  setAcking]  = useState(false);
  const [toast,   setToast]   = useState("");

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3000); };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/compliance/`, ax());
      setDocs(r.data || []);
    } catch { flash("Failed to load documents"); }
    finally { setLoading(false); }
  };
  useEffect(()=>{ load(); },[]);

  const openDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/compliance/${doc.id}`, ax());
      setViewing(r.data);
    } catch { flash("Failed to load document"); }
  };

  const acknowledge = async () => {
    if(!viewing) return;
    setAcking(true);
    try {
      await axios.post(`${API}/compliance/${viewing.id}/acknowledge`, {}, ax());
      setViewing(v=>({...v, user_acked:true}));
      setDocs(ds=>ds.map(d=>d.id===viewing.id?{...d,user_acked:true}:d));
      flash("✅ Document acknowledged");
    } catch { flash("Failed to acknowledge"); }
    finally { setAcking(false); }
  };

  const categories = [...new Set(docs.map(d=>d.category))];
  const filtered   = docs.filter(d=>{
    const q = search.toLowerCase();
    return (!search||d.title.toLowerCase().includes(q)||d.category.toLowerCase().includes(q))
      && (catFilter==="all"||d.category===catFilter);
  });
  const grouped = {};
  filtered.forEach(d=>{ (grouped[d.category]||(grouped[d.category]=[])).push(d); });

  const pending = docs.filter(d=>d.is_mandatory&&!d.user_acked).length;

  const card = {
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
  };

  const inp = (extra={}) => ({
    width:"100%", padding:"9px 12px",
    border:`1px solid ${T.border}`, borderRadius:8,
    background:T.inp_bg, color:T.text,
    fontFamily:"Outfit,sans-serif", fontSize:13, outline:"none", ...extra,
  });

  return (
    <div style={{ fontFamily:"Outfit,sans-serif", color:T.text }}>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
          letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>
          DOCUMENTS
        </div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px", color:T.text }}>
          Company Policies
        </h2>
        <p style={{ fontSize:13.5, color:T.muted, margin:0 }}>
          Read and acknowledge HR policies and legal documents
        </p>
      </div>

      {/* Pending banner */}
      {pending > 0 && (
        <div style={{ padding:"14px 18px",
          background:`${T.amber}14`,
          border:`1px solid ${T.amber}40`,
          borderLeft:`4px solid ${T.amber}`,
          borderRadius:10, marginBottom:20,
          display:"flex", alignItems:"center", gap:12 }}>
          <AlertTriangle size={18} color={T.amber}/>
          <div>
            <div style={{ fontSize:13.5, fontWeight:700, color:T.amber }}>
              {pending} mandatory document{pending!==1?"s":" "} require your acknowledgement
            </div>
            <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
              Please read and acknowledge all mandatory policies below
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:22 }}>
        {[
          { label:"Total Policies",  val:docs.length,                                          color:T.cyan,   icon:<FileText size={16}/> },
          { label:"Acknowledged",    val:docs.filter(d=>d.user_acked).length,                  color:T.green,  icon:<CheckCircle size={16}/> },
          { label:"Pending Read",    val:docs.filter(d=>d.is_mandatory&&!d.user_acked).length, color:T.amber,  icon:<AlertTriangle size={16}/> },
        ].map(s=>(
          <div key={s.label} style={{ ...card, padding:"14px 18px",
            borderTop:`3px solid ${s.color}`,
            display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ color:s.color }}>{s.icon}</div>
            <div>
              <div style={{ fontSize:26, fontWeight:900, color:s.color,
                lineHeight:1, fontFamily:"JetBrains Mono,monospace" }}>{s.val}</div>
              <div style={{ fontSize:11.5, color:T.muted, marginTop:3,
                textTransform:"uppercase", letterSpacing:"0.06em" }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Search + filter */}
      <div style={{ display:"flex", gap:10, marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ position:"relative", flex:1, minWidth:200 }}>
          <Search size={13} style={{ position:"absolute", left:11, top:11, color:T.muted }}/>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search policies..."
            style={{ ...inp(), paddingLeft:34 }}/>
        </div>
        <select value={catFilter} onChange={e=>setCat(e.target.value)}
          style={{ ...inp(), width:"auto", cursor:"pointer", paddingRight:24 }}>
          <option value="all" style={{background:T.card}}>All Categories</option>
          {categories.map(c=><option key={c} style={{background:T.card}}>{c}</option>)}
        </select>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign:"center", padding:60, color:T.muted }}>
          Loading policies...
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:60, ...card }}>
          <Shield size={40} style={{ color:T.dim, marginBottom:10 }}/>
          <div style={{ fontSize:15, fontWeight:700, color:T.text }}>No documents found</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:4 }}>
            Try changing your search or filter
          </div>
        </div>
      ) : Object.entries(grouped).map(([cat, catDocs])=>{
        const color = CAT_COLOR[cat] || T.muted;
        return (
          <div key={cat} style={{ marginBottom:28 }}>
            {/* Category header */}
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12,
              paddingBottom:10, borderBottom:`1px solid ${T.border}` }}>
              <span style={{ fontSize:18 }}>{CAT_ICONS[cat]||"📄"}</span>
              <span style={{ fontSize:14, fontWeight:800, color:T.text }}>{cat}</span>
              <span style={{ fontSize:11.5, padding:"2px 9px", borderRadius:100,
                background:`${color}18`, color, fontWeight:700 }}>
                {catDocs.length}
              </span>
            </div>

            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {catDocs.map(doc=>{
                const needsRead = doc.is_mandatory && !doc.user_acked;
                return (
                  <div key={doc.id} onClick={()=>openDoc(doc)}
                    style={{
                      ...card,
                      padding:"16px 20px", cursor:"pointer",
                      borderLeft:`3px solid ${needsRead ? T.amber : doc.user_acked ? T.green : T.dim}`,
                      transition:"all .2s",
                      display:"flex", alignItems:"center", gap:16,
                    }}
                    onMouseEnter={e=>{
                      e.currentTarget.style.borderColor=T.cyan;
                      e.currentTarget.style.background=T.card_hover;
                    }}
                    onMouseLeave={e=>{
                      e.currentTarget.style.borderColor = needsRead?T.amber:doc.user_acked?T.green:T.dim;
                      e.currentTarget.style.background = T.card;
                    }}>

                    {/* Icon */}
                    <div style={{ width:44, height:44, borderRadius:10, flexShrink:0,
                      background:`${color}12`, border:`1px solid ${color}20`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:22 }}>
                      {CAT_ICONS[cat]||"📄"}
                    </div>

                    {/* Content */}
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center",
                        gap:8, flexWrap:"wrap", marginBottom:4 }}>
                        <span style={{ fontSize:14, fontWeight:700, color:T.text }}>
                          {doc.title}
                        </span>
                        {needsRead && (
                          <span style={{ fontSize:10, padding:"2px 8px", borderRadius:100,
                            background:`${T.amber}18`, color:T.amber, fontWeight:700,
                            display:"flex", alignItems:"center", gap:3 }}>
                            <AlertTriangle size={9}/> READ REQUIRED
                          </span>
                        )}
                        {doc.user_acked && (
                          <span style={{ fontSize:10, padding:"2px 8px", borderRadius:100,
                            background:`${T.green}15`, color:T.green, fontWeight:700,
                            display:"flex", alignItems:"center", gap:3 }}>
                            <CheckCircle size={9}/> ACKNOWLEDGED
                          </span>
                        )}
                      </div>
                      {doc.summary && (
                        <div style={{ fontSize:12.5, color:T.muted, lineHeight:1.5,
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {doc.summary}
                        </div>
                      )}
                      <div style={{ display:"flex", gap:10, marginTop:5 }}>
                        <span style={{ fontSize:11, color:T.dim }}>v{doc.version}</span>
                        {doc.effective_date && (
                          <span style={{ fontSize:11, color:T.dim,
                            display:"flex", alignItems:"center", gap:3 }}>
                            <Clock size={9}/>{doc.effective_date}
                          </span>
                        )}
                      </div>
                    </div>

                    <ChevronRight size={16} color={T.dim} style={{ flexShrink:0 }}/>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ══ DOCUMENT VIEWER MODAL ══ */}
      {viewing && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center", padding:16,
          backdropFilter:"blur(4px)" }}
          onClick={e=>{if(e.target===e.currentTarget)setViewing(null);}}>
          <div style={{ background:T.card, border:`1px solid ${T.cyan}40`,
            borderRadius:16, width:"100%", maxWidth:760,
            maxHeight:"90vh", overflow:"hidden",
            display:"flex", flexDirection:"column",
            boxShadow:`0 24px 80px rgba(0,0,0,0.6)` }}>

            {/* Viewer header */}
            <div style={{ padding:"20px 24px 14px",
              borderBottom:`1px solid ${T.border}`,
              background:`linear-gradient(135deg,${T.cyan}0D,transparent)`,
              display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                  <span style={{ fontSize:24 }}>{CAT_ICONS[viewing.category]||"📄"}</span>
                  <h3 style={{ fontSize:18, fontWeight:800, margin:0, color:T.text }}>
                    {viewing.title}
                  </h3>
                </div>
                <div style={{ display:"flex", gap:7, flexWrap:"wrap" }}>
                  {[
                    {l:viewing.category,              c:CAT_COLOR[viewing.category]||T.muted},
                    {l:`Version ${viewing.version}`,  c:T.dim},
                    viewing.effective_date && {l:`Effective ${viewing.effective_date}`, c:T.cyan},
                    viewing.is_mandatory   && {l:"Mandatory Read", c:T.amber},
                  ].filter(Boolean).map(({l,c})=>(
                    <span key={l} style={{ fontSize:11, padding:"3px 10px", borderRadius:100,
                      background:`${c}18`, color:c, fontWeight:600 }}>{l}</span>
                  ))}
                </div>
              </div>
              <button onClick={()=>setViewing(null)}
                style={{ border:"none", background:T.toggle_bg,
                  borderRadius:"50%", width:32, height:32, cursor:"pointer",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  color:T.muted, flexShrink:0 }}>
                <X size={15}/>
              </button>
            </div>

            {/* Content */}
            <div style={{ padding:"24px 32px", overflowY:"auto", flex:1 }}>
              {viewing.summary && (
                <div style={{ padding:"12px 18px",
                  background:`${T.cyan}0F`,
                  border:`1px solid ${T.cyan}26`,
                  borderLeft:`4px solid ${T.cyan}`,
                  borderRadius:10, marginBottom:24,
                  fontSize:14, color:T.text,
                  lineHeight:1.65, fontStyle:"italic" }}>
                  {viewing.summary}
                </div>
              )}

              {/* Render HTML content */}
              <div
                className="doc-content"
                dangerouslySetInnerHTML={{ __html: viewing.content }}
                style={{ fontSize:14, lineHeight:1.85, color:T.muted }}
              />

              <style>{`
                .doc-content h2{font-size:18px;font-weight:800;color:${T.text};
                  margin:20px 0 8px;padding-bottom:6px;border-bottom:1px solid ${T.border};}
                .doc-content h3{font-size:15px;font-weight:700;color:${T.cyan};margin:16px 0 6px;}
                .doc-content p{margin:8px 0;color:${T.muted};}
                .doc-content strong,.doc-content b{color:${T.text};font-weight:700;}
                .doc-content em,.doc-content i{color:${T.muted};font-style:italic;}
                .doc-content ul{padding-left:22px;margin:10px 0;}
                .doc-content ol{padding-left:22px;margin:10px 0;}
                .doc-content li{margin:6px 0;color:${T.muted};}
                .doc-content hr{border:none;border-top:1px solid ${T.border};margin:20px 0;}
                .doc-content u{text-decoration:underline;text-underline-offset:3px;}
              `}</style>
            </div>

            {/* Footer — acknowledge */}
            <div style={{ padding:"16px 24px",
              borderTop:`1px solid ${T.border}`,
              background:T.surface_soft,
              display:"flex", justifyContent:"space-between",
              alignItems:"center", gap:12, flexWrap:"wrap" }}>

              <div style={{ fontSize:12.5 }}>
                {viewing.user_acked ? (
                  <span style={{ color:T.green, fontWeight:700,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <CheckCircle size={14}/>
                    You have acknowledged this document
                  </span>
                ) : viewing.is_mandatory ? (
                  <span style={{ color:T.amber,
                    display:"flex", alignItems:"center", gap:6 }}>
                    <AlertTriangle size={13}/>
                    Please read this mandatory policy and acknowledge below
                  </span>
                ) : (
                  <span style={{ color:T.muted }}>
                    Click acknowledge once you have read this document
                  </span>
                )}
              </div>

              {!viewing.user_acked && (
                <button onClick={acknowledge} disabled={acking}
                  style={{ padding:"10px 24px", borderRadius:9, border:"none",
                    background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                    color:"#fff", cursor:acking?"not-allowed":"pointer",
                    fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800,
                    display:"flex", alignItems:"center", gap:7, whiteSpace:"nowrap",
                    opacity:acking?.7:1,
                    boxShadow:`0 4px 16px ${T.cyan}4D` }}>
                  <CheckCircle size={14}/>
                  {acking ? "Saving..." : "I have read & acknowledge"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%",
          transform:"translateX(-50%)", padding:"10px 22px",
          borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")
            ?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:T.shadow,
          fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}